import { h, Component, Prop, Element, State } from '@stencil/core';

import { LiveIndicatorSizePreset, LiveIndicatorVariantsPreset } from '../../../services/interfaces';
import { getTextContent } from '../../../services/utils';

/**
 * @part base - The `<div>` HTML element used as the base container.
 * @part dot - The `<span>` HTML element used used to simulate the pulsing dot.
 * @part text - The `<span>` HTML element used to display the text.
 */
@Component({
  tag: 'pk-live-indicator',
  styleUrl: './scss/pk-live-indicator.scss',
  shadow: true,
})
export class PkLive {
  // Own Properties
  // ====================

  private textElem: HTMLSpanElement;

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkLiveIndicatorElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  @State() hasText: boolean = false;

  // Public Property API
  // ========================

  /** If true, the border will be removed from the stroke variant */
  @Prop({ mutable: true }) noBorder: boolean;

  /** If true, the inner padding will be removed from the live indicator */
  @Prop({ mutable: true }) noPadding: boolean;

  /** The variant style to apply */
  @Prop({ reflect: true }) variant: LiveIndicatorVariantsPreset = 'filled';

  /** The size of the live indicator */
  @Prop({ reflect: true }) size: LiveIndicatorSizePreset = 'S';

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  connectedCallback() {
    this.handleTextSlotChange();
  }

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleTextSlotChange = () => {
    const slot = this.textElem?.querySelector('slot') ?? null;

    this.hasText = slot && !!getTextContent(slot);
  };

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    return (
      <div
        class={{
          [`pk-live-indicator pk-live-indicator--${this.variant}`]: true,
          'border border-solid border-[color:--live-indicator--background]':
            this.variant === 'stroke' && !this.noBorder,
          'size--m': this.size === 'M',
          'no-text': !this.hasText,
          'p-0': this.noPadding,
        }}
        part="base"
      >
        <span
          class={{
            ['m-[calc(var(--live-indicator-dot--size)/2)] h-[--live-indicator-dot--size] w-[--live-indicator-dot--size] scale-100 animate-[live-pulse_2s_infinite] rounded-full bg-[--live-indicator-dot--background] shadow-[0_0_0_0_--live-indicator-dot--background]']:
              true,
            'ml-0': this.hasText,
          }}
          part="dot"
        />
        <span
          class={{
            ['text-[length:--live-indicator--font-size] leading-[--live-indicator--line-height] text-[color:--live-indicator--text]']:
              true,
            hidden: !this.hasText,
          }}
          ref={(el: HTMLSpanElement) => (this.textElem = el)}
          part="text"
        >
          <slot onSlotchange={this.handleTextSlotChange}></slot>
        </span>
      </div>
    );
  }
}
